import { APP_SECRET_KEY } from '@src/runtimeConfig'
/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import CryptoJS from 'crypto-js'

import { myToaster } from '@interstellar-component'

import { LoginService } from './service'
import { useApp } from '../../AppContext'
import { encryptPassword, myToasterFromApi } from '@src/services/Helper'

const LoginContext = createContext()

function LoginProvider({ children }) {
  const { getSession } = useApp()
  const [, setCookie] = useCookies(['token-backoffice', 'refresh-token-backoffice'])
  const [User, setUser] = useState()
  // Tracks whether this LoginProvider instance is still mounted. Login flips the
  // auth cookie which swaps App.jsx from unauthenticated → authenticated, unmounting
  // the unauth LoginProvider while the login() promise is still resolving. Without
  // this guard, the trailing `.then`/`.catch` callbacks fire myToaster again from
  // the stale closure, producing phantom "Login successful." toasts on later
  // navigations (esp. clicking Settings tabs while the toast is still on screen).
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])
  const [isProfileSliderOpen, setIsProfileSliderOpen] = useState(false)
  const [currentModal, setCurrentModal] = useState({
    status: false,
    current: null,
    error: null,
  })

  const handleCurrentModal = useCallback((modal) => {
    if (modal && modal.current) {
      setCurrentModal({
        status: true,
        current: modal.current,
        parent_id: modal.parent_id,
        error: modal.error,
      })
    } else {
      setCurrentModal((e) => ({ ...e, current: null }))
      setTimeout(() => {
        setCurrentModal({ current: null })
      }, 200)
    }
  }, [])

  const login = async (body) => {
      const encryptedPassword = encryptPassword(body.password)
      const payload = {
        email: body.email,
        password: encryptedPassword,
      }

      try {
        const result = await LoginService.login(payload)

        // Toast + persist auth state ONLY while this LoginProvider is still
        // mounted. After setCookie below, App.jsx swaps auth branches and unmounts
        // this provider; any further firing of myToaster from a stale closure
        // is suppressed.
        if (!isMountedRef.current) return
        myToaster(result)

        if (result?.user_id) {
          localStorage.setItem('RrwF57&aRMoR5Eq23#Mi', result.user_id)
        }
        setCookie('token-backoffice', result?.data?.access_token, { path: '/' })
        setCookie('refresh-token-backoffice', result?.data?.refresh_token, {
          path: '/',
          maxAge: body?.remember_me ? 30 * 24 * 60 * 60 : 7 * 24 * 60 * 60,
        })

        if (body?.remember_me === true) {
          const rememberMeData = { email: body.email, password: body.password }
          localStorage.setItem(
            'rv5zzc9noTdU5AD2',
            CryptoJS.AES.encrypt(
              JSON.stringify(rememberMeData),
              APP_SECRET_KEY,
            ).toString(),
          )
        } else {
          localStorage.removeItem('rv5zzc9noTdU5AD2')
        }
      } catch (e) {
        if (!isMountedRef.current) return
        myToasterFromApi(e)
        // Unverified account with the right password: the API sent a fresh
        // code and returned the user id, so reopen the signup wizard on Verify
        // (Register resumes at register_active_step >= 3 when the id is set).
        if (e?.status === 403 && e?.data?.email_not_verified && e?.data?.user_id) {
          localStorage.setItem('RrwF57&aRMoR5Eq23#Mi', e.data.user_id)
          localStorage.setItem('register_active_step', '3')
          if (e.data.countdown_to_new_otp) {
            localStorage.setItem('countdown_to_new_otp', e.data.countdown_to_new_otp)
          }
          window.location.assign('/register')
        }
      }
    }
    

  const getUser = useCallback(
    async () =>
      await LoginService.detail()
        .then((res) => {
          setUser({ data: res.data })
        })
        .catch(myToasterFromApi),
    []
  )

  const changePassword = useCallback(async (body) => {
    console.log(body)
    const formData = new FormData()
    formData.append('old_password', body.old_password)
    formData.append('new_password', body.new_password)
    return await LoginService.changePassword(formData)
      .then((e) => {
        console.log('MASUK KE THEEN?')
        myToaster(e)
        setIsProfileSliderOpen(false)
        getSession()
      })
      .catch((e) => {
        myToasterFromApi(e)
        throw e
      })
  }, [])

  const updateProfile = useCallback(
    async (body, shouldChangePassword) => {
      // console.log(body);
      const formData = new FormData()
      formData.append('photo', body.photo)
      if (body.delete_photo) {
        formData.append('delete_photo', body.delete_photo)
      } else {
        formData.append('delete_photo', false)
      }
      formData.append('name', body.name)
      formData.append('username', body.username)
      formData.append('email', body.email)
      formData.append('whatsapp', body.whatsapp)
      return await LoginService.update(formData)
        .then(myToaster)
        .then(getUser)
        .then(getSession)
        .then(shouldChangePassword ? () => {} : setIsProfileSliderOpen(false))
        .catch(myToasterFromApi)
    },
    [getSession, getUser]
  )

  const contextValue = useMemo(
    () => ({
      login,
      getUser,
      User,
      updateProfile,
      changePassword,
      isProfileSliderOpen,
      setIsProfileSliderOpen,
      currentModal,
      handleCurrentModal,
    }),
    [
      login,
      getUser,
      updateProfile,
      User,
      changePassword,
      isProfileSliderOpen,
      setIsProfileSliderOpen,
      currentModal,
      handleCurrentModal,
    ]
  )

  return <LoginContext.Provider value={contextValue}>{children}</LoginContext.Provider>
}

const useLogin = () => {
  const context = useContext(LoginContext)
  if (context === undefined) {
    throw new Error('useLogin must be used within a LoginProvider')
  }
  return context
}

export { LoginProvider, useLogin }
